const stripInteractiveElements = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  doc.querySelectorAll("[data-citation], .citation-button, button").forEach(el => el.remove());

  return doc.body.innerHTML;
};

const htmlToPlainText = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const assertClipboardAvailable = (): void => {
  if (!navigator.clipboard) {
    throw new Error("Clipboard API is not available");
  }
};

export const copyTextToClipboard = async (text: string): Promise<void> => {
  assertClipboardAvailable();
  await navigator.clipboard.writeText(text);
};

export const copyHtmlToClipboard = async (html: string, plainText?: string): Promise<void> => {
  assertClipboardAvailable();

  const cleanHtml = stripInteractiveElements(html);
  const text = plainText ?? htmlToPlainText(cleanHtml);

  if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
    const item = new ClipboardItem({
      "text/html": new Blob([cleanHtml], { type: "text/html" }),
      "text/plain": new Blob([text], { type: "text/plain" }),
    });
    await navigator.clipboard.write([item]);
  } else {
    await navigator.clipboard.writeText(text);
  }
};

export const copyAsRichText = async (element: HTMLElement, plainText?: string): Promise<void> => {
  const html = element.innerHTML;
  await copyHtmlToClipboard(html, plainText);
};

const SVG_FALLBACK_WIDTH = 800;
const SVG_FALLBACK_HEIGHT = 600;

const canWriteClipboardImages = (): boolean => typeof ClipboardItem !== "undefined" && !!navigator.clipboard?.write;

const dataUrlToPngBlob = (dataUrl: string): Blob => {
  const binaryStr = atob(dataUrl.split(",")[1]);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
  return new Blob([bytes], { type: "image/png" });
};

const writePngToClipboard = async (dataUrl: string): Promise<void> => {
  await navigator.clipboard.write([new ClipboardItem({ "image/png": dataUrlToPngBlob(dataUrl) })]);
};

const containsSvg = (html: string): boolean => /<svg[\s>]/i.test(html);

const normalizeSvgDimensions = (svg: SVGSVGElement): { width: number; height: number } => {
  const parsePixelValue = (value: string | null): number | null => {
    if (!value || value.endsWith("%")) return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  const widthAttr = svg.getAttribute("width");
  const heightAttr = svg.getAttribute("height");
  const explicitWidth = parsePixelValue(widthAttr);
  const explicitHeight = parsePixelValue(heightAttr);

  if (explicitWidth !== null && explicitHeight !== null) {
    return { width: explicitWidth, height: explicitHeight };
  }

  const viewBox = svg.getAttribute("viewBox");
  if (viewBox) {
    const parts = viewBox.trim().split(/[\s,]+/);
    if (parts.length === 4) {
      const vbWidth = parseFloat(parts[2]);
      const vbHeight = parseFloat(parts[3]);
      if (!isNaN(vbWidth) && !isNaN(vbHeight)) {
        return { width: vbWidth, height: vbHeight };
      }
    }
  }

  return { width: SVG_FALLBACK_WIDTH, height: SVG_FALLBACK_HEIGHT };
};

export const copySvgAsImage = async (html: string, scale?: number): Promise<void> => {
  if (!canWriteClipboardImages()) {
    await copyHtmlToClipboard(html);
    return;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const svgEl = doc.querySelector("svg");

  if (!svgEl) {
    await copyHtmlToClipboard(html);
    return;
  }

  const { width, height } = normalizeSvgDimensions(svgEl as SVGSVGElement);
  svgEl.setAttribute("width", String(width));
  svgEl.setAttribute("height", String(height));

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgEl);
  const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to load SVG as image"));
      img.src = svgUrl;
    });

    const pixelRatio = scale ?? window.devicePixelRatio ?? 1;
    const canvas = document.createElement("canvas");
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas 2D context");

    ctx.scale(pixelRatio, pixelRatio);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    await writePngToClipboard(canvas.toDataURL("image/png"));
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
};

const CAPTURE_TIMEOUT_MS = 3000;

const containsScript = (html: string): boolean => /<script[\s>]/i.test(html);

export const captureIframeAsImage = async (iframe: HTMLIFrameElement): Promise<void> => {
  if (!canWriteClipboardImages()) {
    throw new Error("Clipboard image write is not supported");
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(() => {
      window.removeEventListener("message", handler);
      reject(new Error("Iframe capture timed out"));
    }, CAPTURE_TIMEOUT_MS);

    const handler = (e: MessageEvent) => {
      if (e.data?.type !== "artefact-captured" || e.source !== iframe.contentWindow) return;
      clearTimeout(timeout);
      window.removeEventListener("message", handler);
      if (e.data.dataUrl) {
        resolve(e.data.dataUrl);
      } else {
        reject(new Error("Iframe capture returned no image data"));
      }
    };

    window.addEventListener("message", handler);
    iframe.contentWindow?.postMessage({ type: "artefact-capture" }, "*");
  });

  await writePngToClipboard(dataUrl);
};

export const copyArtefactContent = async (content: string, iframe?: HTMLIFrameElement | null): Promise<void> => {
  if (containsSvg(content)) {
    await copySvgAsImage(content);
  } else if (containsScript(content) && iframe) {
    await captureIframeAsImage(iframe);
  } else {
    await copyHtmlToClipboard(content);
  }
};
